/**
 * TicketDetailHeader — top app-bar of the 3-panel ticket view.
 * Shows: back button, ticket # / status / SLA badge,
 * Reopen (if resolved), quick-status select, details-panel toggle, refresh.
 */
import React from 'react';
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StatusBadge, SlaBadge } from './TicketComponents';

export function TicketDetailHeader({
  selectedTicket,
  isStaff,
  user,
  updatingStatus,
  showRightPanel,
  setShowRightPanel,
  setMobileDetailOpen,
  onBack,
  onReopen,
  onUpdateStatus,
  onRefresh,
}) {
  const staff = typeof isStaff === 'function' ? isStaff() : !!isStaff;
  return (
    <div className="border-b border-slate-200 bg-white px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="flex items-center gap-1 sm:gap-2 flex-shrink-0"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Back to Tickets</span>
          <span className="sm:hidden">Back</span>
        </Button>
        {selectedTicket && (
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap min-w-0">
            <Badge variant="outline" className="bg-slate-900 text-white border-slate-900 font-mono text-xs">
              #{selectedTicket.ticket_number}
            </Badge>
            <StatusBadge status={selectedTicket.status} />
            <SlaBadge
              breached={selectedTicket.sla_breached}
              paused={selectedTicket.sla_paused}
              dueAt={selectedTicket.sla_due_at}
              hours={selectedTicket.sla_hours}
            />
            {selectedTicket.internal_only && (
              <Badge
                variant="outline"
                className="bg-amber-100 text-amber-800 border-amber-300 text-[11px] font-semibold gap-1"
                title="Agency-only. Account Admin / User at this location cannot see this ticket."
                data-testid="header-internal-badge"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                INTERNAL
              </Badge>
            )}
            {selectedTicket.on_behalf_of_actor_name && (
              <Badge
                variant="outline"
                className="bg-violet-50 text-violet-800 border-violet-200 text-[11px] gap-1"
                title={`Created by ${selectedTicket.on_behalf_of_actor_name} on behalf of ${selectedTicket.created_by_name}`}
                data-testid="header-onbehalf-badge"
              >
                On behalf of {selectedTicket.created_by_name}
              </Badge>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Reopen — visible when resolved, for staff OR the creator */}
        {selectedTicket && selectedTicket.status === 'resolved' &&
          (staff || selectedTicket.created_by_id === user?.id) && (
            <Button
              size="sm"
              onClick={onReopen}
              disabled={updatingStatus}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="reopen-ticket-btn"
              title="Reopen this ticket"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Reopen
            </Button>
          )}

        {/* Quick status update */}
        {selectedTicket && staff && (
          <Select
            value={selectedTicket.status}
            onValueChange={onUpdateStatus}
            disabled={updatingStatus}
          >
            <SelectTrigger className="h-8 w-32 sm:w-40 text-xs" data-testid="quick-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="waiting_on_customer">Waiting on Customer</SelectItem>
              <SelectItem value="under_investigation">Under Investigation</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
        )}

        {/* Details panel toggle */}
        {selectedTicket && (
          <>
            <Button
              variant={showRightPanel ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setShowRightPanel(!showRightPanel)}
              className="hidden lg:inline-flex"
              data-testid="toggle-right-panel-desktop"
              title={showRightPanel ? 'Hide details' : 'Show details'}
            >
              {showRightPanel ? (
                <>
                  <ChevronRight className="w-4 h-4 mr-1" />
                  Hide details
                </>
              ) : (
                <>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Show details
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMobileDetailOpen(true)}
              className="lg:hidden"
              data-testid="toggle-right-panel-mobile"
              title="Show ticket details"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Details
            </Button>
          </>
        )}

        <Button onClick={onRefresh} variant="outline" size="sm" data-testid="refresh-ticket-btn" aria-label="Refresh tickets">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default TicketDetailHeader;
